/**
 * AppErrorBoundary — application-level React error boundary.
 *
 * PURPOSE: Diagnostic containment only.
 * This boundary catches uncaught render errors that escape all inner boundaries
 * (e.g. WorkspaceOperationalBoundary) and replaces the blank-page failure mode
 * with a named, recoverable error UI.
 *
 * The blank-screen root cause is currently UNPROVEN.  This boundary is
 * instrumentation — it captures the exact exception name and component stack
 * so that the next occurrence produces actionable evidence rather than a silent
 * blank page.  It does NOT claim to fix the underlying crash.
 *
 * Evidence collection: the full error + componentStack are printed to
 * console.error with the [AppErrorBoundary] prefix so they are visible in
 * browser DevTools and any server-side log forwarder.
 */
import React from "react";

export class AppErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, info) {
    // Preserve evidence — do NOT silence these logs.
    // The component stack is the primary artifact needed to close the
    // blank-screen UNPROVEN chain; capture it unconditionally.
    console.error("[AppErrorBoundary] Uncaught render error:", error);
    console.error("[AppErrorBoundary] Component stack:", info?.componentStack);
    this.setState({ componentStack: info?.componentStack ?? null });
  }

  render() {
    if (!this.state.hasError) {
      return this.props.children;
    }

    return (
      <div style={{
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: "system-ui, -apple-system, sans-serif",
        background: "#f8fafc",
        padding: "2rem",
      }}>
        <div style={{ maxWidth: "560px", width: "100%" }}>
          <div style={{
            display: "flex", alignItems: "center", gap: "0.5rem",
            marginBottom: "0.75rem",
          }}>
            <div style={{
              width: "8px", height: "8px", borderRadius: "50%",
              background: "#f97316", flexShrink: 0,
            }} />
            <span style={{ fontSize: "0.7rem", fontWeight: 700, letterSpacing: "0.08em",
              textTransform: "uppercase", color: "#94a3b8" }}>
              Application error
            </span>
          </div>
          <h2 style={{
            fontSize: "1.1rem", fontWeight: 600, color: "#1e293b",
            margin: "0 0 0.5rem",
          }}>
            Something went wrong
          </h2>
          <p style={{ fontSize: "0.875rem", color: "#64748b", margin: "0 0 1.25rem", lineHeight: 1.5 }}>
            {this.state.error?.message || "An unexpected error occurred."}
          </p>

          {/* Diagnostic detail — intentionally visible to support staff */}
          {this.state.componentStack && (
            <details style={{ marginBottom: "1.25rem" }}>
              <summary style={{
                fontSize: "0.75rem", color: "#94a3b8", cursor: "pointer",
                userSelect: "none",
              }}>
                Technical detail (for support)
              </summary>
              <pre style={{
                fontSize: "0.7rem", color: "#475569",
                background: "#f1f5f9", padding: "0.75rem",
                borderRadius: "0.375rem", overflow: "auto",
                maxHeight: "200px", marginTop: "0.5rem",
                whiteSpace: "pre-wrap", wordBreak: "break-all",
                border: "1px solid #e2e8f0",
              }}>
                {this.state.error?.name}: {this.state.error?.message}
                {"\n\n"}
                {this.state.componentStack}
              </pre>
            </details>
          )}

          <button
            onClick={() => window.location.reload()}
            style={{
              padding: "0.5rem 1.25rem",
              background: "#1e293b",
              color: "#fff",
              border: "none",
              borderRadius: "0.5rem",
              fontSize: "0.875rem",
              fontWeight: 500,
              cursor: "pointer",
              transition: "background 0.15s",
              marginRight: "0.5rem",
            }}
            onMouseOver={(e) => { e.currentTarget.style.background = "#334155"; }}
            onMouseOut={(e) => { e.currentTarget.style.background = "#1e293b"; }}
          >
            Reload page
          </button>
          {/* Safe-route escape — avoids re-crash loop if the crashing route is deterministic */}
          <button
            onClick={() => { window.location.href = "/"; }}
            style={{
              padding: "0.5rem 1.25rem",
              background: "transparent",
              color: "#64748b",
              border: "1px solid #e2e8f0",
              borderRadius: "0.5rem",
              fontSize: "0.875rem",
              fontWeight: 500,
              cursor: "pointer",
            }}
          >
            Go to dashboard
          </button>
        </div>
      </div>
    );
  }
}
